import React, { useCallback, useState } from "react";
import { mergeRight } from "ramda";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import DialogContent from "@mui/material/DialogContent";
import { lensProp, set } from "ramda";
import Grid from "@mui/material/Grid";
import { FetchError } from "../util/fetchUtils";
import { useDispatch } from "react-redux";
import { logOut } from "../auth/authActions";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useApplicationAccountKeystoreCreate } from "./applicationAccountServices";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const initialState = {
  fields: {
    notes: "",
  },
};

const CreateKeystoreDialog = ({ account, open, onClose, onSave }) => {
  const dispatch = useDispatch();
  const [fields, setFields] = useState(initialState.fields);
  const [errorAlert, setErrorAlert] = useState({ open: false });

  const onFieldChanged = field => (event, optVal) => {
    const value = event.target.value || optVal;
    const update = set(lensProp(field), value, fields);
    setFields(update);
  };

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const handleError = (message, err) => {
    if (err instanceof FetchError) {
      if (err.status === 401) {
        dispatch(logOut());
      } else {
        showAlert(message);
      }
    } else {
      showAlert(err.message);
    }
  };

  const createKeystore = useApplicationAccountKeystoreCreate();
  const handleOnSave = async () => {
    try {
      await createKeystore.mutateAsync({
        data: fields,
        resourceIds: { id: account.applicationId },
      });
      onSave(fields);
    } catch (err) {
      handleError("Unable to create Key Pair.", err);
    } finally {
      setFields(initialState.fields);
    }
  };

  const handleOnClose = useCallback(() => {
    setFields(initialState);
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">Optional Notes</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              id="notes"
              label="Notes"
              fullWidth
              value={fields.notes || ""}
              onChange={onFieldChanged("notes")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleOnSave} color="primary">
          Save
        </Button>
      </DialogActions>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CreateKeystoreDialog;
